import { useState, useContext, useEffect, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useParams, useNavigate, replace } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { format, parseISO } from "date-fns";
import { MenuContext } from "src/context/MenuContext";
import { useAuth } from "src/hooks/useAuth";
import { getProjects } from "src/store/project";
import { getMcode } from "src/store/mcode";
import {addDailyWorks, getDailyWorksByDate,getSingleDailyWorks,deleteSingleData,} from "src/store/schedule";
import ScheduleInputValidationSchema from "src/validation/ScheduleInputValidationSchema";
import Menu from "src/components/common/Menu";
import Header from "src/components/common/Header";
import Popup from "src/components/schedule/SchedulePopup";
import CustomDatePicker from "src/components/common/CustomDatePicker";
import { ValidationMessages } from "src/components/common/ValidationMessage";
import ScrollTop from "src/components/common/ScrollTop";
import TaskList from "src/components/schedule/ScheduleInputTask";
import MemoSection from "src/components/schedule/ScheduleInputMemo";
import { getUsers } from "src/store/user";
import CustomLoader from "src/components/common/CustomLoader";
import "src/css/CustomDatePicker.css";
import TimeSelector from "src/components/common/TimeSelector"

export default function ScheduleInput() {
  const [actionSource, setActionSource] = useState('normal');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: userData } = useAuth();
  const { visibleMenu } = useContext(MenuContext);

  const copyData = useSelector((state) => state.schedule.data);
  const projectList = useSelector((state) => state.project.data);
  const workingHoursCd = useSelector((state) => state.mcode.data.workinghourscd);
  const holidayData = useSelector((state) => state.mcode.data.holiday);
  const user = useSelector((state) => state.user.data);
  const edit = useSelector((state) => state.schedule.singleData);
  const [myMemo, setMyMemo] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isSpecipicCalOpen, setSpecipicCalOpen] = useState(false);
  const [holiday_cd, setHolidayCd] = useState(null);
  const [validationMessages, setValidationMessages] = useState([]);
  const [datePickerPurpose, setDatePickerPurpose] = useState("work_date");
  const [isCopyAction, setIsCopyAction] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSpecificDay, setSelectedSpecificDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id, work_date } = useParams();
  const workDateCalendarRef = useRef(null);
  const specificDayCalendarRef = useRef(null);
  const [showTimeSelector, setShowTimeSelector] = useState(false);

  const isValidDate = (date) => {
    return date instanceof Date && !isNaN(date.getTime());
  };

  const [date, setDate] = useState(() => {
    return work_date && isValidDate(parseISO(work_date)) ? format(parseISO(work_date), "yyyy-MM-dd"): format(new Date(), "yyyy-MM-dd");     
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ScheduleInputValidationSchema),
    mode: "onSubmit",
    defaultValues: {
      work_date: date,
      work_start_time: "",
      daily_memo: "",
      tasks: [],
      holidayArea: [],
    },
  });

  const {
    fields: tasks,
    append: appendTask,
    remove: removeTask,
    replace: replaceTask,
  } = useFieldArray({ control, name: "tasks" });

  const {
    fields: holidayArea,
    append: appendHoliday,
    remove: removeHoliday,
    replace: replaceHoliday,
  } = useFieldArray({
    control,
    name: "holidayArea",
  });

  const handleClickOutside = (event) => {
    if (workDateCalendarRef.current && !workDateCalendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
        setShowTimeSelector(false);
    }
    if (specificDayCalendarRef.current && !specificDayCalendarRef.current.contains(event.target)){
        setSpecipicCalOpen(false);
        
    } 
     
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => { document.removeEventListener("mousedown", handleClickOutside);};
      
  }, []);

  useEffect(() => {
    if (work_date) {
      const parsedDate = parseISO(work_date);
      if (isValidDate(parsedDate)) {
        setDate(format(parsedDate, "yyyy-MM-dd"));
        setValue("work_date", format(parsedDate, "yyyy/MM/dd EEEE")); // Synchronize with form
      } else {
        console.error("Invalid work_date:", work_date);
        const fallbackDate = format(new Date(), "yyyy-MM-dd");
        setDate(fallbackDate);
        setValue("work_date", format(new Date(), "yyyy/MM/dd EEEE")); // Synchronize with form
      }
    } else {
      const fallbackDate = format(new Date(), "yyyy-MM-dd");
      setDate(fallbackDate);
      setValue("work_date", format(new Date(), "yyyy/MM/dd EEEE")); // Synchronize with form
    }
  }, [work_date, setValue]);

  useEffect(() => {
    dispatch(getProjects());
    dispatch(getMcode({ section_cd: "workinghourscd,holiday" }));
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    const data = {
      t_user_id: userData?.id ?? null,
      work_date: date,
      copy_type: "specific",
    };
    dispatch(getDailyWorksByDate(data)).then((response) => {
      if (response.error?.message) {
        setValidationMessages(response.error.message);
      }
      if (response.payload?.errors) {
        setValidationMessages(response.payload?.errors);
      }
      if (response.payload) {
        setLoading(false);
      }
    });
    setIsCopyAction(true);
  }, [date, dispatch, userData?.id]);

  useEffect(() => {
    setLoading(true);
    let initialDate = work_date ? new Date(work_date) : new Date();
    if (isNaN(initialDate.getTime())) {
      initialDate = new Date();
    }
    const formattedDate = format(initialDate, "yyyy-MM-dd");
    dispatch(getSingleDailyWorks({ id, work_date: formattedDate })).then(
      (response) => {
        if (response.error?.message) {
          setValidationMessages(response.error.message);
        }
        if (response.payload?.errors) {
          setValidationMessages(response.payload.errors);
        }
        if (response.payload) {
          setLoading(false);
        }
      }
    );
  }, [dispatch, id, work_date]);

  useEffect(() => {
    if (userData && workingHoursCd) {
      const matchedWorkingHours = workingHoursCd.find(
        (item) => item.cd === userData.working_hours_cd
      );
      if (matchedWorkingHours) {
        setValue("work_start_time", matchedWorkingHours.code_value1);
      }
    }
  }, [userData, workingHoursCd, setValue]);

  useEffect(() => {
    const extractErrors = (errorObj) => {
      let messages = new Set(); // Use Set() to store unique values

      for (const [key, value] of Object.entries(errorObj)) {
        if (value?.message) {
          messages.add(value.message); // Use .add() instead of .push()
        } else if (typeof value === "object") {
          for (const msg of extractErrors(value)) {
            messages.add(msg); // Ensure unique messages
          }
        }
      }

      return messages;
    };

    const messagesSet = extractErrors(errors);
    setValidationMessages([...messagesSet]); // Convert Set back to an array
  }, [errors]);

  useEffect(() => {
    if (copyData && isCopyAction && actionSource !== 'popup') {
      setValue("daily_memo", copyData.daily_memo || "");
      const holiday_cd = copyData.holiday_cd || null;
      setHolidayCd(holiday_cd);

      // Handle tasks first
      const copyTaskList = copyData.task_list || [];
      const copyType = copyData.copy_type || "";

      // If copy type is previous and task list is empty, skip task replacement
      if (copyType === "previous" && (!copyTaskList || copyTaskList.length === 0)) {
        replaceTask([]);
        replaceHoliday([]);
        setHolidayCd(null);
        setValidationMessages([]);
        return;
      }

      const formattedTasks = copyTaskList
        .filter((task) => task.t_project_id !== 0 && task.t_client_id !== 0)
        .map((task) => ({
          project_id: task.t_project_id,
          client_id: task.t_client_id,
          client_name: task.client_name_abbreviation || "",
          project_name: task.project_name_abbreviation || "",
          memo: task.task_detail || "",
          progress_rate: task.progress_rate || 0,
          work_minute: task.work_minute || 0,
          work_cost_price: task.work_cost_price || 0,
          order_no: task.order_no,
          id_Api: task?.id,
        }));

      replaceTask(formattedTasks);

      // Handle holiday data
      if (holiday_cd && holiday_cd !== 0) {
        const matchingHoliday = Array.isArray(holidayData)
          ? holidayData.find((holiday) => String(holiday.cd) === String(holiday_cd))
          : null;

        const leaveTask = copyTaskList.find(
          (task) => task.t_project_id === 0 && task.t_client_id === 0
        );

        if (matchingHoliday) {
          const holidayArea = {
            holiday_cd: holiday_cd,
            holiday_name: matchingHoliday.code_value1,
            memo: leaveTask?.task_detail || "",
            t_daily_work_task_id: leaveTask?.id || null,
          };
          replaceHoliday([holidayArea]); 
        } else {
          replaceHoliday([]); 
        }
      } else {
        replaceHoliday([]); 
        setHolidayCd(null);
      }

      setValidationMessages([]);
    }
  }, [copyData, setValue, replaceTask, replaceHoliday, isCopyAction, projectList, holidayData, actionSource]);
    
  useEffect(() => {
    if (validationMessages.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validationMessages]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    let loginId = userData ? JSON.parse(userData).id : null;
    const userMatch = user.find((val) => val.id === loginId);
    if (userMatch) {
      setMyMemo(userMatch.my_memo || '');
    } else {
      setMyMemo('');
    }
  }, [user]);

  const togglePopup = () => {
    setActionSource('popup');
    setIsCopyAction(false);
    setIsPopupOpen(!isPopupOpen);
  };

  const addTask = (selectedProject) => {
    if (!selectedProject || !selectedProject.id) {
      console.error("Invalid project selected");
      return;
    }

    // Find the full project details from projectList
    const clientProject = projectList.find((client) =>
      client.projects.some((p) => p.id === selectedProject.id)
    );

    if (!clientProject) {
      console.error("Project details not found");
      return;
    }

    const projectDetails = clientProject.projects.find((p) => p.id === selectedProject.id);
      
    const newTask = {
      project_id: selectedProject.id,
      client_id: clientProject.client_id,
      client_name: clientProject.client_name,
      project_name: projectDetails.name_abbreviation,
      holiday_cd: null,
      memo: "",
      progress_rate: 0,
      work_minute: 0,
      work_cost_price: 0,
    };
    
    setActionSource('normal'); // Reset action source after adding task
    appendTask(newTask);
  };

  const addHoliday = (selectedHoliday) => {
    setHolidayCd(selectedHoliday.value);
  
    const newHoliday = {
      holiday_cd: selectedHoliday.value,
      holiday_name: selectedHoliday.label,
      memo: "",
    };
    // Clear existing
    removeHoliday(); 
    // Add the new one
    appendHoliday(newHoliday);
  };
  
  

  const copyMemoToDaily = () => {
    setValue("daily_memo", myMemo);
  };

  const handleDateChange = (selectedDate) => {
    if (!isValidDate(selectedDate)) {
      console.error("Invalid selected date:", selectedDate);
      alert("The selected date is invalid. Please choose a valid date.");
      return;
    }
     replaceHoliday([]);
     replaceTask([]);
    const formattedDate = format(selectedDate, "yyyy-MM-dd");
    const formattedDateWithDay = format(selectedDate, "yyyy/MM/dd EEEE");

    // Update the form and state for "work_date"

    setValue("work_date", formattedDateWithDay);
    setDate(formattedDate);
    setSelectedDate(selectedDate);

    // Only close calendar if it's a date selection, not a month change
    if (datePickerPurpose === "work_date") {
      setIsCalendarOpen(false);
    }

    // Fetch data for the new date
    const data = {
      t_user_id: userData?.id ?? null,
      work_date: formattedDate,
      copy_type: "specific",
    };
    setIsCopyAction(true);
    dispatch(getDailyWorksByDate(data)).then((response) => {
      if (response.error?.message) {
        setValidationMessages(response.error.message);
      }
      if (response.payload?.errors) {
        setValidationMessages(response.payload.errors);
      }
    });
  };

  const handleSpecificDaySelection = (selectedDate) => {
    setActionSource('copy');
    if (!isValidDate(selectedDate)) {
      console.error("Invalid selected date:", selectedDate);
      alert("The selected date is invalid. Please choose a valid date.");
      return;
    }
    replaceHoliday([]);
    setSelectedSpecificDay(selectedDate);
    const formattedDate = format(selectedDate, "yyyy-MM-dd");

    const data = {
      t_user_id: userData?.id ?? null,
      work_date: formattedDate,
      copy_type: "specific",
    };

    setLoading(true);
    dispatch(getDailyWorksByDate(data))
      .then((response) => {
        if (response.payload?.data) {
          const copyData = response.payload.data;
          setValue("daily_memo", copyData.daily_memo || "");

          const copyTaskList = copyData.task_list || [];

          // Filter out holiday task (which has both IDs as 0)
          const nonHolidayTasks = copyTaskList.filter(
            (task) => !(task.t_project_id === 0 && task.t_client_id === 0));
          
          
          const formattedTasks = nonHolidayTasks.map((task, index) => ({
            project_id: task.t_project_id || 0,
            client_id: task.t_client_id || 0,
            client_name: task.client_name_abbreviation || "Unknown Client",
            project_name: task.project_name_abbreviation || "Unknown Project",
            memo: task.task_detail || "",
            work_minute: task.work_minute || 0,
            work_cost_price: task.work_cost_price || 0,
            order_no: task.order_no || index + 1,
            // id_Api: task?.id, 
          }));
          
          replaceTask(formattedTasks);
          
          const holiday_cd = copyData.holiday_cd || null;
          setHolidayCd(holiday_cd);

          const matchingHoliday = Array.isArray(holidayData)
            ? holidayData.find(
                (holiday) => String(holiday.cd) === String(holiday_cd)
              )
            : null;

            const holidayTasks = copyData?.task_list?.filter(
              (task) => task.t_client_id === 0 && task.t_project_id === 0
            ) || [];
            
            if (holiday_cd && holidayTasks.length > 0) {
              const holidayTask = holidayTasks[0]; // Assuming only one holiday task is allowed
            
              const holidayArea = {
                holiday_cd: holiday_cd,
                holiday_name: matchingHoliday ? matchingHoliday.code_value1 : "",
                memo: holidayTask.task_detail || "",
                // t_daily_work_task_id: holidayTask.id, 
              };
            
              replaceHoliday([holidayArea]);
            }
              
          setValidationMessages([]);
          setSpecipicCalOpen(false); 
        } else {
          alert("No data found for the selected date.");
        }
      })
      .catch((error) => {
        alert("An error occurred while fetching data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchPreviousDayData = (data) => {
    setLoading(true);
    dispatch(getDailyWorksByDate(data))
      .then((response) => {
        if (response.payload?.errors) {
          alert(response.payload.errors);
          return;
        }

        const fetchedData = response.payload?.data;
        if (fetchedData) {
          const taskList = fetchedData.task_list || [];
          const formattedTasks = taskList
            .filter(task => task.t_client_id !== 0 || task.t_project_id !== 0)
            .map((task) => ({
              project_id: task.t_project_id || 0,
              client_id: task.t_client_id || 0,
              client_name: task.client_name_abbreviation || "Unknown Client",
              project_name: task.project_name_abbreviation || "Unknown Project",
              memo: task.task_detail || "",
              work_minute: task.work_minute || 0,
              work_cost_price: task.work_cost_price || 0,
              
            }));
            
          replaceTask(formattedTasks);
  
          // Handle holiday
          const holiday_cd = fetchedData.holiday_cd || null;
          setHolidayCd(holiday_cd);
          
          // Only create holiday area if holiday_cd exists and is not 0
          if (holiday_cd && holiday_cd !== 0) {
            const matchingHoliday = Array.isArray(holidayData)
              ? holidayData.find((holiday) => String(holiday.cd) === String(holiday_cd))
              : null;
  
            const leaveTask = taskList.find(task =>
              task.t_client_id === 0 && task.t_project_id === 0
            );
  
            if (matchingHoliday) {
              const holidayArea = {
                holiday_cd: holiday_cd,
                holiday_name: matchingHoliday.code_value1,
                memo: leaveTask?.task_detail || ""
              };
              // Always pass an array to replaceHoliday
              replaceHoliday([holidayArea]);
            } else {
              replaceHoliday([]); 
            }
          } else {
            replaceHoliday([]); 
          }
  
          setValidationMessages([]);
        } else {
          setValue("daily_memo", "");
          replaceTask([]);
          replaceHoliday([]); 
          setHolidayCd(null);
          alert("No previous data found.");
        }
      })
      .catch((error) => {
        console.error("Error fetching previous day data:", error);
        alert("An error occurred while fetching data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  const PreviouDayCopy = () => {
    setActionSource('copy');
    const watchedDate = watch("work_date");
  
    let currentDate;

    if (typeof watchedDate === "string") {
      const datePart = watchedDate.split(" ")[0]; 
      const isoDateString = datePart.replace(/\//g, "-"); 
  
      try {
        currentDate = parseISO(isoDateString);
        if (!isValidDate(currentDate)) {
          throw new Error("Invalid date");
        }
      } catch (error) {
        alert("Invalid date format: " + datePart);
        return;
      }
    } else if (watchedDate instanceof Date && isValidDate(watchedDate)) {
      currentDate = watchedDate; 
    } else {
      currentDate = new Date(); 
    }
  
    // Normalize the date to midnight in UTC
    currentDate = new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()));
  
    // Calculate the previous day
    const previousDay = new Date(currentDate);
   
    if (isValidDate(previousDay)) {
      const formattedDate = format(previousDay, "yyyy-MM-dd");
  
      const data = {
        t_user_id: userData?.id ?? null,
        work_date: formattedDate,
        copy_type: "previous",
        allowPreviousCopy: true,
      };
  
      setIsCopyAction(false);
      fetchPreviousDayData(data);
    } else {
      alert("Error calculating previous day");
    }
  };

  const onSubmit = (data) => {
    if (
      (!data.tasks || data.tasks.length === 0) &&
      (!data.holidayArea || data.holidayArea.length === 0)
    ) {
      setValidationMessages(["Priority Task or Holiday Area is required."]);
      return;
    }

    // Extract only the date part (e.g., "2025/04/04") from "2025/04/04 Friday"
    const work_date_string = data.work_date.split(" ")[0]; // Split by space and take the first part
    const parsedDate = parseISO(work_date_string.replace(/\//g, "-")); // Replace "/" with "-" to match ISO format

    if (!isValidDate(parsedDate)) {
      return;
    }

    const [hours, minutes] = data.work_start_time?.split(":") || [];
    if (!hours || !minutes) {
      return;
    }

    parsedDate.setHours(hours, minutes);
    const formattedDate = parsedDate
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");

    let tasksData = data.tasks.map((task) => ({
      cl_id: task.client_id ?? 0,
      pr_id: task.project_id ?? 0,
      memo: task.memo ?? null,
      progress_rate: task.progress_rate ?? null,
      work_minute: task.work_minute ?? null,
      work_cost_price: task.work_cost_price ?? null,
      order_no: task.order_no ?? 1,
    }));

    if (data.holidayArea?.length > 0) {
      data.holidayArea.forEach((holiday) => {
        tasksData.push({
          cl_id: 0,
          pr_id: 0,
          memo: holiday.memo || null,
          progress_rate: null,
          work_minute: null,
          work_cost_price: null,
          order_no: tasksData.length + 1,
        });
      });
    }

    const formattedWorkDate = format(parsedDate, "yyyy/MM/dd");
    const submitData = {
      t_user_id: userData?.id ?? null,
      work_date: formattedWorkDate,
      work_start_time: data.work_start_time || "9:00:00",
      daily_memo: data.daily_memo || null,
      task_details: tasksData,
      holiday_cd: holiday_cd || null,
      work_start_datetime: formattedDate,
      my_memo: myMemo,
    };

    setLoading(true);
    dispatch(addDailyWorks(submitData))
      .then((response) => {
        if (response.payload) {
          setLoading(false);
        }
        if (response.payload.errors) {
          const messagesArray = Object.values(response.payload.errors).map(
            (error) => error
          );
          setValidationMessages(messagesArray);
        }
        if (addDailyWorks.fulfilled.match(response)) {
          navigate(`/scheduleList/${date}`);
        }
      })
      .catch((error) => {
        alert(error);
      });
  };

  const deleteSingleTask = async (taskId) => {
    const userConfirmed = window.confirm(
      "Are you sure you want to delete this task?"
    );
    if (!userConfirmed) return;
  
    setLoading(true);
  
    try {
      const response = await dispatch(deleteSingleData(taskId));
      let payload = response.payload;
  
      if (typeof payload === "string") {
        try {
          const jsonMatch = payload.match(/\{.*\}/);
          if (jsonMatch) {
            payload = JSON.parse(jsonMatch[0]);
          } else {
            throw new Error("Failed to extract JSON from payload.");
          }
        } catch (error) {
          console.error("Failed to parse payload:", error);
          setLoading(false);
          return;
        }
      }
  
      if (payload?.message === "Record deleted successfully") {
        // Clear holiday states immediately if it's a holiday task
        setHolidayCd(null);
        replaceHoliday([]);

        // Then fetch updated data
        const updatedData = await dispatch(
          getDailyWorksByDate({
            t_user_id: userData?.id ?? null,
            work_date: date,
            copy_type: "specific",
          })
        );
  
        const data = updatedData.payload?.data;
  
        if (data) {
          // Handle regular tasks
          const taskList = data.task_list || [];
          const formattedTasks = taskList
            .filter(task => task.t_client_id !== 0 || task.t_project_id !== 0)
            .map((task, index) => ({
              project_id: task.t_project_id || 0,
              client_id: task.t_client_id || 0,
              client_name: task.client_name_abbreviation || "Unknown Client",
              project_name: task.project_name_abbreviation || "Unknown Project",
              memo: task.task_detail || "",
              progress_rate: task.progress_rate || 0,
              work_minute: task.work_minute || 0,
              work_cost_price: task.work_cost_price || 0,
              order_no: index + 1,
              id_Api: task?.id,
            }));
  
          replaceTask(formattedTasks);
  
          // Only update holiday if it exists in the response
          const holiday_cd = data.holiday_cd || null;
          if (holiday_cd && holiday_cd !== 0) {
            const matchingHoliday = Array.isArray(holidayData)
              ? holidayData.find((holiday) => String(holiday.cd) === String(holiday_cd))
              : null;
  
            const leaveTask = taskList.find(task =>
              task.t_client_id === 0 && task.t_project_id === 0
            );
  
            if (matchingHoliday && leaveTask) {
              const holidayArea = {
                holiday_cd: holiday_cd,
                holiday_name: matchingHoliday.code_value1,
                memo: leaveTask?.task_detail || "",
                t_daily_work_task_id: leaveTask?.id || null
              };
              replaceHoliday([holidayArea]);
              setHolidayCd(holiday_cd);
            }
          }
        }
        alert(payload.message); // Show success message after state updates
      }
    } catch (error) {
      setValidationMessages([
        error.message || "An error occurred while deleting the task.",
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleHolidayRemove = (index) => {
    removeHoliday(index);
    setHolidayCd(null);    
    setActionSource('normal');  
  };

  return (
    <>
      <div className="schedule-input">
        <div className="wrapper">
          {visibleMenu && <Menu />}
          <div className={visibleMenu ? "menu-open" : "menu-closed"}>
            <div className="navbar navbar-fixed-top navbar-absolute">
              <Header sx={{ mb: 4 }} />
            </div>
            {loading ? (
              <CustomLoader />
            ) : (
              <section className="container-fluid contents-top">
                <form onSubmit={handleSubmit(onSubmit)}>
                  {validationMessages && validationMessages.length > 0 && (
                    <div style={{ background: "#ffe4e2" }}>
                      <ValidationMessages
                        messages={validationMessages}
                        onClose={() => setValidationMessages([])}
                      />
                    </div>
                  )}
                  <div className="form-inline" id="search_form">
                    <div className="row calender-box">
                      <div className="col-xs-1"></div>
                      <div className="col-xs-10 text-center">
                        <h1 className="input-group datepicker-hl jq-datetimepicker">
                          <input
                            type="text"
                            name="work_date"
                            value={
                              isValidDate(parseISO(date))
                                ? format(parseISO(date), "yyyy/MM/dd EEEE") // Display date and day
                                : "Invalid Date"
                            }
                            readOnly
                            onClick={() => {
                              setDatePickerPurpose("work_date");
                              setIsCalendarOpen(!isCalendarOpen);
                            }}
                            {...register("work_date")}
                          />
                          <span
                            className="input-group-addon"
                            onClick={() => {
                              setDatePickerPurpose("work_date");
                              setIsCalendarOpen(!isCalendarOpen);
                              setSpecipicCalOpen(false);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <span className="glyphicon glyphicon-calendar"></span>
                          </span>
                          {isCalendarOpen && (
                            <div  ref={workDateCalendarRef} style={{ position: "absolute", zIndex: 1000 }}>             
                              <CustomDatePicker
                                workDate={selectedDate}                    
                                handleDateChange={handleDateChange}
                                isValidDate={isValidDate}
                              />
                            </div>
                          )}
                        </h1>
                      </div>
                      <div className="col-xs-1 text-right"></div>
                    </div>
                    <div className="row">
                     <div className="col-xs-12 col-sm-6 text-left">
                        <div
                          className="form-group"
                          style={{ display: "inline-block" }}
                        >
                          <label className="sr-only" htmlFor="InputTime1">
                            出勤時刻
                          </label>
                          <p className="form-control"  onClick={() => setShowTimeSelector(!showTimeSelector)} style={{ margin: 0, cursor: 'pointer' }}>{watch("work_start_time")}</p>
                          {showTimeSelector && (
                            <div ref={workDateCalendarRef} >
                              <TimeSelector
                                value={watch("work_start_time")}
                                onChange={(e) => {
                                  setValue("work_start_time", e.target.value);
                                  
                                }}
                              />
                            </div>
                          )}
                          {errors.work_start_time && (
                            <p className="text-danger">
                              {errors.work_start_time.message}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="col-xs-12 col-sm-6 input_menu text-right">
                        <button
                          type="button"
                          className="btn btn-default jq-popup-open"
                          style={{ marginRight: "5px" }}
                          onClick={togglePopup}
                        >
                          <span
                            className="glyphicon glyphicon-plus"
                            aria-hidden="true"
                          ></span>
                        </button>
                        <button
                          id="jq_copy_btn"
                          type="button"
                          className="btn btn-success"
                          style={{ marginRight: "5px" }}
                          onClick={PreviouDayCopy}
                        >
                          Previous Day Copy
                        </button>
                        <div
                          style={{
                            display: "inline-block",
                            position: "relative",
                            marginRight: "5px",
                          }}
                        >
                          <button
                            id="jq_copy_assign_btn"
                            type="button"
                            className="btn btn-success"
                            onClick={() => {
                              setSpecipicCalOpen(!isSpecipicCalOpen);
                              setIsCalendarOpen(false);
                            }}
                          >
                            Specific Day Copy
                          </button>
                          {isSpecipicCalOpen && (
                            <div
                              ref={specificDayCalendarRef}
                              style={{ position: "absolute", zIndex: 1000 }}
                              className="calender-align"
                            >
                              <CustomDatePicker
                                workDate={selectedSpecificDay || (isValidDate(parseISO(watch("work_date")))? parseISO(watch("work_date")): new Date())}
                                handleDateChange={handleSpecificDaySelection}
                                isValidDate={isValidDate}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <textarea
                      style={{ display: "none" }}
                      name="copy_my_memo"
                      id="copy_my_memo"
                      value={myMemo || ''}
                      readOnly
                    ></textarea>
                  </div>
                  <section className="container">
                    <div className="work_form" id="work_form">
                      <div className="row">
                        <div className="holiday_area">
                          <input
                            type="hidden"
                            value="33018"
                            name="daily_work_id"
                          />
                        </div>
                          <TaskList tasks={tasks} holidayArea={holidayArea} removeTask={removeTask} removeHoliday={handleHolidayRemove} register={register} control={control} errors={errors} handleSliderChange={() => {}} deleteSingleTask={deleteSingleTask}  isEditMode={false}/>        
                      </div>
                      <MemoSection register={register} myMemo={myMemo}  setMyMemo={setMyMemo} copyMemoToDaily={copyMemoToDaily} isEditMode={false}/> 
                      <div className="col-xs-12 form-button">
                        <button type="submit" className="btn btn-success btn-lg btn-block jq_regist_btn">Save</button>  
                      </div>
                    </div>
                  </section>
                </form>
                {isPopupOpen && (<Popup isOpen={isPopupOpen} togglePopup={togglePopup} addTask={addTask} projectList={projectList} existProject={tasks} addHoliday={addHoliday}/>)}                     
              </section>
            )}
          </div>
        </div>
      </div>
      <ScrollTop />
    </>
  );
}
